import { ConsentFormDefinition } from '../types';

export const therapyTelehealthConsent: ConsentFormDefinition = {
  formKey: 'therapy-telehealth-consent',
  title: 'Therapy Telehealth Consent',
  pdfTitle: 'Therapy Telehealth Consent Form',
  intro:
    'This consent is for all telehealth services provided to me by {providerName} through the HIPAA LINK secure telehealth platform.',
  sections: [
    {
      id: 'patient',
      navLabel: 'Patient Info',
      heading: 'Your Information',
      blocks: [
        {
          type: 'field-group',
          layout: '2-1',
          fields: [
            {
              type: 'text',
              id: 'firstName',
              label: 'First Name',
              placeholder: 'First name',
              required: true,
            },
            {
              type: 'text',
              id: 'middleInitial',
              label: 'Middle Initial (optional)',
              placeholder: 'M.I.',
            },
            {
              type: 'text',
              id: 'lastName',
              label: 'Last Name',
              placeholder: 'Last name',
              required: true,
            },
            {
              type: 'date',
              id: 'dob',
              label: 'Date of Birth',
              required: true,
            },
          ],
        },
      ],
    },
    {
      id: 'what-is',
      navLabel: 'What Is Telehealth?',
      heading: 'What Is Therapy Telehealth?',
      blocks: [
        {
          type: 'paragraph',
          text: 'Telehealth therapy allows you to talk with your therapist or counselor from home or another private location using video, phone, or secure messaging.',
        },
        {
          type: 'paragraph',
          text: 'How you connect:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'Video (see and hear each other)',
            'Phone (hear each other only)',
            'Secure messaging (non-urgent questions between sessions)',
          ],
        },
      ],
    },
    {
      id: 'benefits',
      navLabel: 'Benefits',
      heading: 'Benefits of Telehealth Therapy',
      blocks: [
        {
          type: 'bullet-list',
          items: [
            'No need to travel to an office',
            'More flexible appointment times',
            'Visit from home or another safe, private place',
            'Less missed time from work or other activities',
            'Same quality therapy as in-person visits',
          ],
        },
      ],
    },
    {
      id: 'risks',
      navLabel: 'Risks & Limits',
      heading: 'Possible Risks and Limits',
      blocks: [
        {
          type: 'bullet-list',
          items: [
            'Internet connection problems may interrupt your session',
            'Your therapist cannot examine you physically',
            'Video or audio quality may sometimes be poor',
            'Your therapist may decide you need an in-person visit instead',
            'If someone is nearby, they might hear part of your session',
            'There is a small risk someone could try to access your session without permission, even though we use secure technology',
          ],
        },
      ],
    },
    {
      id: 'privacy',
      navLabel: 'Privacy',
      heading: 'Privacy During Your Sessions',
      blocks: [
        {
          type: 'paragraph',
          text: 'Your responsibilities:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'Choose a private, quiet place for each session where others cannot hear you',
            'Tell people nearby that you are in a private counseling session and should not be interrupted',
            'Use headphones or keep the volume low so others nearby cannot hear',
            'Wear clothing you would wear to an in-person appointment',
            'Do not record or take screenshots of your session without your therapist’s permission',
          ],
        },
        {
          type: 'paragraph',
          text: 'What we do to protect your privacy:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'We use secure, encrypted technology to protect your information',
            'We do not record sessions unless you agree in writing',
            'Your therapist keeps your information confidential following professional and legal requirements',
            'We tell you if anyone else from our office can see or hear your session',
          ],
        },
      ],
    },
    {
      id: 'technology',
      navLabel: 'Technology',
      heading: 'What You Need for Telehealth',
      blocks: [
        {
          type: 'bullet-list',
          items: [
            'A device: computer, tablet, or smartphone',
            'Camera and microphone (usually built into devices)',
            'Internet connection (WiFi or cellular data)',
            'Headphones are recommended for privacy',
            'A private space where you can speak freely',
          ],
        },
        {
          type: 'paragraph',
          text: 'Before each session, test your technology 5-10 minutes early to check your connection and camera.',
        },
      ],
    },
    {
      id: 'rights',
      navLabel: 'Your Rights',
      heading: 'Your Rights',
      blocks: [
        {
          type: 'bullet-list',
          items: [
            'You can stop using telehealth anytime, with or without reason',
            'You can ask for an in-person visit if you prefer',
            'You can ask questions about your therapy and get clear answers',
            'You have the right to see what information is kept about you',
            'You can tell us if you have concerns about your care',
            'You can choose another therapist if you wish',
          ],
        },
      ],
    },
    {
      id: 'confidentiality',
      navLabel: 'Confidentiality',
      heading: 'What Information We Keep Private',
      blocks: [
        {
          type: 'paragraph',
          text: 'We keep everything you tell your therapist confidential. However, we must tell others (or call 911) if:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'You tell us you plan to harm yourself',
            'You tell us you plan to harm someone else',
            'We believe a child is being abused or neglected',
            'A court orders us to share information',
          ],
        },
      ],
    },
    {
      id: 'emergency',
      navLabel: 'Emergency',
      heading: 'What to Do in a Mental Health Emergency',
      blocks: [
        {
          type: 'paragraph',
          text: 'Telehealth is NOT for emergencies. If you are having thoughts of suicide or self-harm, or any other crisis:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'Call 911 or go to the nearest emergency room immediately',
            'Call 988 - National Suicide Prevention Lifeline (available 24/7, call or text)',
            'Text "HELLO" to 741741 - Crisis Text Line (available 24/7)',
            'Call your local crisis services if available',
          ],
        },
        {
          type: 'paragraph',
          text: "For non-emergency issues between sessions, contact your therapist's office during business hours.",
        },
      ],
    },
    {
      id: 'payment',
      navLabel: 'Cost',
      heading: 'Cost of Telehealth Therapy',
      blocks: [
        {
          type: 'bullet-list',
          items: [
            'Your cost depends on your insurance plan',
            'Telehealth visits cost the same as in-person visits',
            'You may owe a copay, deductible, or coinsurance',
            'Ask about costs before your first session',
          ],
        },
      ],
    },
    {
      id: 'understand',
      navLabel: 'Understanding',
      heading: 'Before You Sign',
      blocks: [
        {
          type: 'paragraph',
          text: 'By signing this form, you are saying:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'You understand what telehealth therapy is',
            'You know the benefits, risks, and limits',
            'You have asked all your questions and received answers',
            'You understand what information we keep confidential',
            'You know what to do in a mental health emergency',
            'You want to use telehealth for your therapy',
          ],
        },
        {
          type: 'paragraph',
          text: 'Do NOT sign this form unless you are ready to use telehealth.',
        },
      ],
    },
    {
      id: 'sign',
      navLabel: 'Signature',
      heading: 'Your Signature',
      blocks: [
        {
          type: 'field-group',
          fields: [
            {
              type: 'text',
              id: 'printedName',
              label: 'Your Name (print)',
              placeholder: 'Print your name',
              required: true,
              fullWidth: true,
            },
            {
              type: 'select',
              id: 'relationship',
              label: 'Signing as:',
              options: [
                { value: 'self', label: 'Myself (the client)' },
                { value: 'parent', label: 'Parent of client' },
                { value: 'guardian', label: 'Legal Guardian' },
                { value: 'poa', label: 'Power of Attorney' },
              ],
            },
          ],
        },
        {
          type: 'paragraph',
          text: 'By signing below, I agree to the terms of this consent form and want to use telehealth therapy.',
        },
        { type: 'signature' },
      ],
    },
  ],
  requiredFields: ['firstName', 'lastName', 'dob', 'signature', 'printedName'],
};
